import {
  Box,
  Button,
  CircularProgress,
  Flex,
  FormControl,
  FormErrorMessage,
  HStack,
  Input,
  InputGroup,
  InputLeftAddon,
  PinInput,
  PinInputField,
} from '@chakra-ui/react';
import { maskPhoneNumber } from '@common/utility/masks';
import { phoneNumberRegExUS } from '@common/utility/validations';
import React, { useState } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { TextBlock } from 'src/apps/invest/common/components/TextBlock';
import { useAccountInfoViewModel } from '../models/InfoViewModel';
import { InfoForm } from '../types';

type PhoneNumberFieldProps = {
  infoForm: UseFormReturn<InfoForm>;
  currentPhoneNumber: string | null;
};

type PhoneNumberState = 'DISPLAY' | 'ENTER_NUMBER' | 'ENTER_CODE' | 'LOADING';

export const PhoneNumberField: React.FC<PhoneNumberFieldProps> = ({ infoForm, currentPhoneNumber }) => {
  const [phoneNumberState, setPhoneNumberState] = useState<PhoneNumberState>('DISPLAY');
  const errors = infoForm.formState.errors;
  const viewModel = useAccountInfoViewModel();

  let content;
  switch (phoneNumberState) {
    case 'DISPLAY':
      content = <TextBlock weight="bold">{currentPhoneNumber || ''}</TextBlock>;
      break;
    case 'ENTER_CODE':
      content = (
        <HStack spacing={'5px'}>
          <Controller
            control={infoForm.control}
            name="code"
            rules={{
              maxLength: 6,
              minLength: 6,
              required: 'Pin is required',
            }}
            render={({ field }) => (
              <PinInput
                autoFocus={infoForm.formState.errors.code || !infoForm.formState.isDirty ? true : false}
                type="number"
                placeholder=""
                otp={true}
                value={field.value}
                onComplete={async (value) => {
                  viewModel.setState('LOADING');
                  const formValues = infoForm.getValues();
                  const status = await viewModel.confirmPhoneNumber(value, formValues.phone);
                  if (status.success) {
                    window.location.reload();
                  } else {
                    alert('Unable to verify phone number. Please try again');
                    viewModel.setState('RENDER');
                    setPhoneNumberState('DISPLAY');
                  }
                }}>
                <PinInputField tabIndex={1} />
                <PinInputField tabIndex={2} />
                <PinInputField tabIndex={3} />
                <PinInputField tabIndex={4} />
                <PinInputField tabIndex={5} />
                <PinInputField tabIndex={6} />
              </PinInput>
            )}
          />
        </HStack>
      );
      break;
    case 'ENTER_NUMBER':
      content = (
        <HStack alignItems={'flex-start'} spacing={'15px'} style={{ marginTop: 10 }}>
          <FormControl isInvalid={Boolean(errors.phone)}>
            <InputGroup size="md">
              <InputLeftAddon children="+1" />
              <Input
                autoFocus={true}
                id="phone"
                mb={3}
                placeholder="123-456-7890"
                maxW={['full', 350]}
                defaultValue={infoForm.getValues().phone}
                onInput={maskPhoneNumber}
                {...infoForm.register('phone', {
                  pattern: {
                    message: 'Please use a valid US phone number',
                    value: phoneNumberRegExUS,
                  },
                  required: 'Phone number is required',
                })}
              />
            </InputGroup>

            <FormErrorMessage>{errors.phone && errors.phone.message}</FormErrorMessage>
          </FormControl>
          <Button
            onClick={async () => {
              if (!errors.phone) {
                setPhoneNumberState('LOADING');
                const status = await viewModel.updatePhoneNumber(infoForm.getValues().phone.replace(/[^\d]/g, ''));

                if (status.success) {
                  setPhoneNumberState('ENTER_CODE');
                } else {
                  alert(status.error);
                  setPhoneNumberState('ENTER_NUMBER');
                }
              }
            }}
            style={{ marginLeft: '15px', marginTop: -1 }}>
            Update
          </Button>
        </HStack>
      );
      break;
    case 'LOADING':
      content = (
        <Flex justifyContent="center" alignItems="center" w={'full'} p={10}>
          <CircularProgress isIndeterminate={true} size={'100px'} thickness={'4px'} />
        </Flex>
      );
      break;
  }

  return (
    <Box>
      <HStack justifyContent={'space-between'}>
        <TextBlock color={'gray.600'}>Phone</TextBlock>
        <Button
          onClick={() => {
            if (phoneNumberState === 'DISPLAY') {
              setPhoneNumberState('ENTER_NUMBER');
            } else {
              setPhoneNumberState('DISPLAY');
            }
          }}
          variant="link">
          {phoneNumberState === 'DISPLAY' ? 'Edit' : 'Cancel'}
        </Button>
      </HStack>
      {content}
    </Box>
  );
};
